import React, { useState } from "react";
import { useStore } from "../context/StoreContext";
import { Icons } from "../components/Icons";
import "./Account.css";

/* Friendly messages for common Firebase auth errors */
function authMessage(code) {
  const map = {
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/missing-password": "Please enter your password.",
    "auth/weak-password": "Password should be at least 6 characters.",
    "auth/email-already-in-use": "An account with this email already exists. Please log in.",
    "auth/invalid-credential": "Wrong email or password.",
    "auth/wrong-password": "Wrong email or password.",
    "auth/user-not-found": "No account found with this email. Please sign up.",
    "auth/too-many-requests": "Too many attempts. Please try again in a moment.",
    "auth/network-request-failed": "Network error. Check your connection.",
    "auth/operation-not-allowed":
      "Email/Password sign-in is not enabled in Firebase. Enable it in Authentication settings.",
  };
  return map[code] || "Something went wrong. Please try again.";
}

export default function Account() {
  const {
    currentUser,
    authReady,
    myOrders,
    signup,
    login,
    logout,
    pendingCheckout,
    resumeCheckoutIfPending,
    goShop,
    Rs,
  } = useStore();

  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!email.trim() || !password) {
      setErr("Please enter your email and password.");
      return;
    }
    if (mode === "signup" && !name.trim()) {
      setErr("Please enter your name.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        await signup(name, email, password);
      } else {
        await login(email, password);
      }
      setName(""); setEmail(""); setPassword("");
      // resume checkout if they came here from the cart
      resumeCheckoutIfPending();
    } catch (e) {
      setErr(authMessage(e.code));
    } finally {
      setBusy(false);
    }
  };

  /* ============ LOADING ============ */
  if (!authReady) {
    return (
      <div className="account-page">
        <div className="account-loading">{Icons.diamond(36)}<p>Loading…</p></div>
      </div>
    );
  }

  /* ============ LOGGED IN: profile + orders ============ */
  if (currentUser) {
    const displayName = currentUser.displayName || currentUser.email.split("@")[0];
    return (
      <div className="account-page">
        <div className="page-header">
          <h1>MY ACCOUNT</h1>
          <p>Welcome back, {displayName}</p>
        </div>

        <div className="account-content">
          <div className="account-card profile-card">
            <div className="profile-avatar">{displayName.charAt(0).toUpperCase()}</div>
            <div className="profile-info">
              <div className="profile-name">{displayName}</div>
              <div className="profile-email">{currentUser.email}</div>
            </div>
            <button className="btn-logout" onClick={logout}>Log Out</button>
          </div>

          <div className="orders-section">
            <h2 className="orders-title">My Orders</h2>

            {myOrders.length === 0 ? (
              <div className="orders-empty">
                {Icons.cart(40)}
                <p>You haven't placed any orders yet.</p>
                <button className="btn-primary" onClick={() => goShop("All")}>
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="orders-list">
                {myOrders.map((o) => (
                  <div key={o.id} className="order-card">
                    <div className="order-head">
                      <span className={`order-status status-${o.status || "placed"}`}>
                        {(o.status || "placed").toUpperCase()}
                      </span>
                      <span className="order-date">
                        {o.createdAt?.seconds
                          ? new Date(o.createdAt.seconds * 1000).toLocaleDateString("en-GB", {
                              day: "numeric", month: "short", year: "numeric",
                            })
                          : "Just now"}
                      </span>
                    </div>

                    <div className="order-items">
                      {(o.items || []).map((it, i) => (
                        <div key={i} className="order-item">
                          {it.img
                            ? <img className="oi-thumb" src={it.img} alt={it.name} loading="lazy" />
                            : <span className="oi-thumb oi-thumb-ph">{Icons.diamond(18)}</span>}
                          <span className="oi-main">
                            <span className="oi-name">{it.name}</span>
                            {(it.color || it.size) && (
                              <span className="oi-attrs">
                                {it.color && (
                                  <span className="oi-color">
                                    <span
                                      className="oi-color-dot"
                                      style={{ background: it.colorHex || "#ccc" }}
                                    />
                                    {it.color}
                                  </span>
                                )}
                                {it.size && <span className="oi-size">Size: {it.size}</span>}
                              </span>
                            )}
                          </span>
                          <span className="oi-qty">x{it.qty}</span>
                          <span className="oi-price">{Rs(it.price * it.qty)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="order-total">
                      <span>Total</span>
                      <strong>{Rs(o.total)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ============ LOGGED OUT: login / signup ============ */
  return (
    <div className="account-page">
      <div className="page-header">
        <h1>{mode === "login" ? "LOGIN" : "CREATE ACCOUNT"}</h1>
        <p>
          {pendingCheckout
            ? "Please log in to complete your checkout"
            : "Access your orders and checkout faster"}
        </p>
      </div>

      <div className="auth-wrapper">
        <div className="auth-card">
          <div className="auth-toggle">
            <button
              className={mode === "login" ? "active" : ""}
              onClick={() => { setMode("login"); setErr(""); }}
            >
              Login
            </button>
            <button
              className={mode === "signup" ? "active" : ""}
              onClick={() => { setMode("signup"); setErr(""); }}
            >
              Sign Up
            </button>
          </div>

          {err && <div className="auth-error">{err}</div>}

          {mode === "signup" && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}

          <input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />

          <button className="btn-auth" onClick={submit} disabled={busy}>
            {busy
              ? "Please wait…"
              : mode === "login"
              ? "Login"
              : "Create Account"}
          </button>

          <div className="auth-switch">
            {mode === "login" ? (
              <span>
                New here?{" "}
                <button onClick={() => { setMode("signup"); setErr(""); }}>
                  Create an account
                </button>
              </span>
            ) : (
              <span>
                Already have an account?{" "}
                <button onClick={() => { setMode("login"); setErr(""); }}>
                  Log in
                </button>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
